var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, Link, Stack } from '@mui/material';
import { flatMap, isEmpty, map, times } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ConfirmDialog, ErrorMessageBox, FallbackComponent, TimezoneSelect } from '../../common';
import { CopyDataFrom } from '../common/copyDataFrom';
import { WizardContext } from '../context';
import { useCancelAndDeleteMultiskill, useQueueAPI, useQueueHasMultiskill, useQueueStatus, useQueuesHasMultiskill, useRefreshQueueData, } from '../queue/selectors';
import { QueueStatusType } from '../queue/types';
import { useIsPlanLocked, usePlanAPI } from '../selectors';
import { useHoursMismatchDialog } from '../workVolume/selectors';
import { ExceptionForm } from './exceptionForm';
import { TimeIntervalSelect } from './intervalSelect';
import { CLOSED, useChangeIntervalAPI, useCreateWapAPI, useDeleteWapAPI, useTimeCopyAPI, useWATGroup, } from './selectors';
import { WORK_ARRIVAL_TABLE_MIN_WIDTH, WorkArrivalTimeTable } from './timeTable';
import { useWapTimezone } from './wapContext';
import { WapHoursMismatchDialog } from './wapHoursMismatchDialog';
var DEFAULT_DAYS = function (month) { return times(7, function (day) { return ({ month: month, day: day, openTime: null, closeTime: null }); }); };
var DEFAULT_ITEMS = { 'month-1': DEFAULT_DAYS(-1) };
var validateForm = function (length) {
    if (length === void 0) { length = 0; }
    var itemShape = {};
    times(length + 1).forEach(function (month) {
        itemShape["month".concat(month - 1)] = yup
            .array()
            .of(yup.object().shape({
            openTime: yup.string().required(),
            closeTime: yup.string().required(),
        }))
            .atLeastOpen(CLOSED);
    });
    return yup.object().shape({
        queueId: yup.number().required(),
        timeInterval: yup.number().required(),
        items: yup.object().shape(itemShape).required(),
    });
};
export var WorkArrivalTimeContent = function (_a) {
    var wats = _a.wats, props = __rest(_a, ["wats"]);
    var watGroup = useWATGroup();
    return (_jsx(_Fragment, { children: map(wats !== null && wats !== void 0 ? wats : watGroup, function (data, month) { return (_jsx(WorkArrivalTimeTable, __assign({ data: data }, props, { month: data[0].month }), month)); }) }));
};
var INITIAL_CONFIRM_VALUES = {
    wap: {},
    source: '',
    clear: false,
};
export var WorkArrivalTimeContainer = function () {
    var _a, _b, _c, _d;
    var t = useTranslation('wizard').t;
    var _e = useState(INITIAL_CONFIRM_VALUES), confirmValues = _e[0], setConfirmValues = _e[1];
    var planData = usePlanAPI()[0];
    var queueData = useQueueAPI()[0];
    var status = useQueueStatus();
    var _f = useWapTimezone(), timezone = _f.timezone, setTimezone = _f.setTimezone;
    var parentForm = useFormContext();
    var isPlanLocked = useIsPlanLocked();
    var setParentTimezone = function (val) {
        if (parentForm) {
            parentForm.setValue('timezone', val || undefined, { shouldDirty: true });
        }
    };
    useEffect(function () {
        if (queueData.data) {
            setTimezone(queueData.data.timezone || 'Etc/UTC');
        }
        // setTimezone is stable from useContext — only re-run when the queue identity changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(_a = queueData.data) === null || _a === void 0 ? void 0 : _a.id]);
    var _g = useForm({
        resolver: yupResolver(validateForm((_b = planData.data) === null || _b === void 0 ? void 0 : _b.periodLength)),
    }), control = _g.control, handleSubmit = _g.handleSubmit, reset = _g.reset, watch = _g.watch, setValue = _g.setValue, getValues = _g.getValues, isValid = _g.formState.isValid;
    var _h = watch().items, items = _h === void 0 ? {} : _h;
    var _j = useCreateWapAPI(), createdData = _j[0], createWAP = _j[1], clearCreatedData = _j[2];
    var _k = useChangeIntervalAPI(), changeIntervalData = _k[0], clearIntervalData = _k[2];
    var _l = useDeleteWapAPI(), deletedData = _l[0], deleteWAP = _l[1], clearDeletedData = _l[2];
    var _m = useTimeCopyAPI(), copiedData = _m[0], copyWAT = _m[1], clearCopyData = _m[2];
    var refreshData = useRefreshQueueData();
    var watGroup = useWATGroup();
    var fetchCancelAndDeleteMultiskill = useCancelAndDeleteMultiskill();
    var hasMultiskill = useQueueHasMultiskill();
    var queuesHasMultiskill = useQueuesHasMultiskill();
    var _o = useHoursMismatchDialog('hoursMismatch:wap'), mismatchDialogOpen = _o.mismatchDialogOpen, mismatchedLocations = _o.mismatchedLocations, setMismatchDialogOpen = _o.setMismatchDialogOpen, fetchMismatch = _o.fetchMismatch;
    var hasWatGroupRef = useRef(false);
    hasWatGroupRef.current = !isEmpty(watGroup);
    var _p = useContext(WizardContext), handleSave = _p.handleSave, isDirty = _p.methods.formState.isDirty;
    useEffect(function () {
        if (queueData.data) {
            reset({
                queueId: queueData.data.id,
                timeInterval: queueData.data.timeInterval,
                items: isEmpty(watGroup) ? DEFAULT_ITEMS : watGroup,
            });
        }
    }, [reset, queueData.data, watGroup]);
    useEffect(function () {
        if (createdData.data || changeIntervalData.data || deletedData.data || copiedData.data) {
            refreshData();
            if (createdData.data || copiedData.data) {
                fetchMismatch(timezone);
            }
        }
        return function () {
            clearCreatedData();
            clearIntervalData();
            clearDeletedData();
            clearCopyData();
        };
    }, [
        changeIntervalData.data,
        clearCopyData,
        clearCreatedData,
        clearDeletedData,
        clearIntervalData,
        copiedData.data,
        createdData.data,
        deletedData.data,
        fetchMismatch,
        refreshData,
        timezone,
    ]);
    useEffect(function () {
        if (!timezone)
            return;
        if (!hasWatGroupRef.current)
            return;
        fetchMismatch(timezone);
    }, [fetchMismatch, timezone]);
    var withDirtyGuard = function (action) {
        if (isDirty) {
            return handleSave('submit', action)();
        }
        action();
    };
    var onClearData = function () {
        withDirtyGuard(function () { var _a; return deleteWAP(undefined, { queueId: (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.id, month: -1 }); });
    };
    var onCopy = function (source) {
        withDirtyGuard(function () { return copyWAT(undefined, { source: source }); });
    };
    var onSaveWAP = function (values) {
        withDirtyGuard(function () { return createWAP(__assign(__assign({}, values), { timezone: timezone, items: flatMap(values.items) })); });
    };
    var handleContinue = function () {
        fetchCancelAndDeleteMultiskill();
        if (confirmValues.clear) {
            onClearData();
        }
        else if (confirmValues.source) {
            onCopy(confirmValues.source);
        }
        else {
            onSaveWAP(confirmValues.wap);
        }
        setConfirmValues(INITIAL_CONFIRM_VALUES);
    };
    var handleSaveWAP = handleSubmit(function (values) {
        var _a;
        if (hasMultiskill || (queuesHasMultiskill && values.timeInterval !== ((_a = queueData.data) === null || _a === void 0 ? void 0 : _a.timeInterval))) {
            setConfirmValues(__assign(__assign({}, confirmValues), { wap: values }));
        }
        else {
            onSaveWAP(values);
        }
    });
    var handleClearData = function () {
        if (hasMultiskill && (status === null || status === void 0 ? void 0 : status.wat)) {
            setConfirmValues(__assign(__assign({}, confirmValues), { clear: true }));
        }
        else {
            onClearData();
        }
    };
    var handleCopy = function (source) {
        if (hasMultiskill) {
            setConfirmValues(__assign(__assign({}, confirmValues), { source: source }));
        }
        else {
            onCopy(source);
        }
    };
    var handleAdd = function (month) {
        if (isDirty) {
            return handleSave('submit', function () { return setValue("items.month".concat(month), DEFAULT_DAYS(month)); })();
        }
        setValue("items.month".concat(month), DEFAULT_DAYS(month));
    };
    return (_jsxs(Stack, __assign({ position: "relative", spacing: 4, component: "form", onSubmit: handleSaveWAP }, { children: [_jsx(ErrorMessageBox, { errors: [createdData.error, changeIntervalData.error, deletedData.error, copiedData.error] }), _jsxs(FallbackComponent, __assign({ requesting: !!createdData.requesting ||
                    !!changeIntervalData.requesting ||
                    !!deletedData.requesting ||
                    !!copiedData.requesting, overlay: true }, { children: [_jsx(Box, __assign({ sx: { overflowX: 'auto' } }, { children: _jsxs(Stack, __assign({ sx: { minWidth: WORK_ARRIVAL_TABLE_MIN_WIDTH }, spacing: 2 }, { children: [_jsxs(Stack, __assign({ direction: "row", gap: 4, alignItems: "center" }, { children: [_jsx(TimeIntervalSelect, { disabled: isPlanLocked, control: control, watch: watch, setValue: setValue, getValues: getValues }), _jsx(TimezoneSelect, { disabled: isPlanLocked, value: timezone, onChange: function (val) {
                                                setTimezone(val);
                                                setParentTimezone(val);
                                            }, label: t('wapTime.timezone'), labelPlacement: "start" }), ((_c = planData.data) === null || _c === void 0 ? void 0 : _c.isBaseline) && (_jsxs(_Fragment, { children: [_jsx(CopyDataFrom, { disabled: isPlanLocked, disableClear: true, statusType: QueueStatusType.WAT, onCopy: handleCopy }), Object.keys(items).length < 2 && (_jsx(Link, __assign({ disabled: isPlanLocked, component: "button", type: "button", onClick: handleClearData, sx: { whiteSpace: 'nowrap' } }, { children: t('copyData.clearData') })))] }))] })), map(items, function (arr, month) { return (_jsx(WorkArrivalTimeTable, { month: arr[0].month, data: arr, control: control, watch: watch, setValue: setValue, getValues: getValues, disableHorizontalScroll: true }, month)); })] })) })), ((_d = planData.data) === null || _d === void 0 ? void 0 : _d.isBaseline) && (_jsxs(Grid, __assign({ container: true, rowSpacing: 2 }, { children: [_jsx(Grid, __assign({ item: true, xs: 12, lg: 8 }, { children: _jsx(ExceptionForm, { selectedMonths: Object.keys(items), onAdd: handleAdd, isDisabled: isPlanLocked }) })), _jsx(Grid, __assign({ item: true, xs: 12, lg: 4, justifyContent: "flex-end", display: "flex", gap: 2 }, { children: _jsx(LoadingButton, __assign({ variant: "outlined", type: "submit", disabled: !isValid || isPlanLocked, loading: createdData.requesting }, { children: t('wapTime.createTable') })) }))] })))] })), _jsx(ConfirmDialog, { open: !!Object.values(confirmValues.wap).length || confirmValues.clear || !!confirmValues.source, onClose: function () { return setConfirmValues(INITIAL_CONFIRM_VALUES); }, message: t('message.confirm'), confirmButtonText: "continue", onConfirm: handleContinue }), _jsx(WapHoursMismatchDialog, { open: mismatchDialogOpen, mismatchedLocations: mismatchedLocations, onClose: function () { return setMismatchDialogOpen(false); } })] })));
};
